import { useEffect, useState } from "react";
import { getEnabledAppPropsByNames } from "../setup/api/services/appProps";
import { AppPropName } from "../setup/api/services/appProps/constants";
import { IAppProp } from "../setup/api/services/appProps/models";

type AppPropsByName = Partial<Record<AppPropName, IAppProp>>;

const CACHE_TTL_MS = 5 * 60 * 1000;

// Module-level cache so every consumer (e.g. each cashout row) shares one
// request per set of names instead of fetching on every mount.
const cache = new Map<
  string,
  { promise: Promise<AppPropsByName>; fetchedAt: number }
>();

const fetchEnabledAppProps = (
  names: AppPropName[]
): Promise<AppPropsByName> => {
  const key = [...names].sort().join(",");
  const cached = cache.get(key);
  if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
    return cached.promise;
  }

  const promise = getEnabledAppPropsByNames(names).then((response) =>
    (response?.data ?? []).reduce<AppPropsByName>((acc, prop) => {
      acc[prop.name as AppPropName] = prop;
      return acc;
    }, {})
  );
  cache.set(key, { promise, fetchedAt: Date.now() });
  // Don't keep a failed request cached, so the next mount retries.
  promise.catch(() => cache.delete(key));
  return promise;
};

// Call after any app-prop write so readers pick up the new value.
export const invalidateAppPropsCache = () => cache.clear();

// Splits a comma-separated list prop, e.g. "WAFFY, Haraj" -> ["WAFFY", "Haraj"].
export const parseAppPropList = (value: string | null | undefined): string[] =>
  (value ?? "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

// Reads a "true"/"false" prop value; anything else counts as false.
export const parseAppPropBoolean = (value: string | null | undefined) =>
  (value ?? "").trim().toLowerCase() === "true";

// Reads enabled app props by name. A name missing from `props` means the prop
// is unknown, disabled or deleted. `props` is null when the request failed.
export const useEnabledAppProps = (
  names: AppPropName[]
): { props: AppPropsByName | null; loading: boolean } => {
  const [props, setProps] = useState<AppPropsByName | null>(null);
  const [loading, setLoading] = useState(true);
  const namesKey = [...names].sort().join(",");

  useEffect(() => {
    let isActive = true;
    setLoading(true);
    fetchEnabledAppProps(namesKey.split(",") as AppPropName[])
      .then((result) => isActive && setProps(result))
      .catch((error) => {
        console.error("Error fetching app props:", error);
        if (isActive) setProps(null);
      })
      .finally(() => isActive && setLoading(false));
    return () => {
      isActive = false;
    };
  }, [namesKey]);

  return { props, loading };
};
